/* ═══════════════════════════════════════════════════════════════
   Forma Format Bar — the model (SYNC_PLAN §3.0). Pure, no React.

   ONE reader and ONE writer for every selectable thing in the Forma Studio,
   so the bar never needs to know which kind it is editing:

     level:N   -> `forma.levels[N-1]`  (font, fontSize, bold, italic,
                  underline, textCase+allCaps, fontColor, justify,
                  spaceBefore, spaceAfter)
     token:*   -> the in-string modifiers the engine, canvas and print
                  preview already honour (bold, italic, underline, size,
                  color, case, sb/sa) — plus `tokenStyles` ONLY for the two
                  fields the modifier syntax lacks (font, align)
     eos       -> `special.eosStyle` (+ `zoneSpace.eos.before`)

   "Follows Typography" means the key is ABSENT; the reader returns the
   Typography value then (the EFFECTIVE value), and a mixed selection comes
   back `mixed` per control, like Word.
   ═══════════════════════════════════════════════════════════════ */

import type { StandardConfig, TextStyle } from '../../shared/types';
import { resolvePageGeometry } from '../../shared/body-layout';
import { parseTokenMods, modSizePt } from '../../shared/zone-layout';
import { eosDragMax, readZoneSpace, writeZoneSpace } from '../../shared/zone-space';
import {
  parseTokenKey,
  readTokenStyle,
  readZone,
  writeTokenStyle,
  zoneApplyToKeys,
} from './formaZonesModel';

export type CaseMode = 'none' | 'upper' | 'title' | 'sentence';
export type AlignMode = 'left' | 'center' | 'right' | 'justify';

export interface Mixable<T> {
  /** Targets disagree. */
  mixed: boolean;
  /** The shared value, or null when mixed / nothing applies. */
  value: T | null;
}

export interface BarReading {
  /** Formattable targets in the selection (levels + tokens + EOS). */
  count: number;
  font: Mixable<string>;
  size: Mixable<number>;
  bold: Mixable<boolean>;
  italic: Mixable<boolean>;
  underline: Mixable<boolean>;
  textCase: Mixable<CaseMode>;
  /** Hex without '#'. */
  color: Mixable<string>;
  align: Mixable<AlignMode>;
  before: Mixable<number>;
  after: Mixable<number>;
  /** Whole-forma line spacing (`docSettings.lineSpacing`). */
  line: Mixable<number>;
}

export type BarEdit =
  | { prop: 'font'; value: string }
  | { prop: 'size'; value: number }
  | { prop: 'bold' | 'italic' | 'underline'; value: boolean }
  | { prop: 'textCase'; value: CaseMode }
  | { prop: 'color'; value: string }
  | { prop: 'align'; value: AlignMode }
  | { prop: 'before' | 'after'; value: number }
  | { prop: 'line'; value: number }
  | { prop: 'reset' };

export const SIZE_MIN = 6;
/** The bar's size field ceiling — the End of Section field it replaces took
 *  up to 72pt, and header/footer token sizes were never capped. Levels keep
 *  their own 16pt cap (`LEVEL_SIZE_MAX`), exactly as the Levels panel had. */
export const SIZE_MAX = 72;
export const LEVEL_SIZE_MAX = 16;
export const SPACE_MAX = 72;
const AUTO_COLOR = '000000';

/* ─── Targets ─── */

interface Targets {
  levels: number[]; // 0-based indices
  tokens: Array<{ key: string; zoneKey: string; idx: number }>;
  eos: boolean;
}

function isTextToken(v: unknown): v is string {
  return typeof v === 'string' && v !== '\n' && !v.startsWith('{Line');
}

function targetsOf(forma: StandardConfig, keys: string[]): Targets {
  const levels: number[] = [];
  const tokens: Targets['tokens'] = [];
  let eos = false;
  for (const k of keys) {
    if (k === 'eos') eos = true;
    else if (k.startsWith('level:')) {
      const i = Number(k.slice(6)) - 1;
      if (forma.levels?.[i]) levels.push(i);
    } else if (k.startsWith('token:')) {
      const { zoneKey, idx } = parseTokenKey(k);
      if (isTextToken(readZone(forma, zoneKey)[idx])) tokens.push({ key: k, zoneKey, idx });
    }
  }
  return { levels, tokens, eos };
}

/* ─── Reading ─── */

function agg<T>(vals: Array<T | undefined>, eq: (a: T, b: T) => boolean): Mixable<T> {
  const have = vals.filter((v): v is T => v !== undefined);
  if (have.length === 0) return { mixed: false, value: null };
  const first = have[0];
  return have.every((v) => eq(v, first))
    ? { mixed: false, value: first }
    : { mixed: true, value: null };
}
const same = <T>(a: T, b: T) => a === b;
const sameCI = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

function caseOfLevel(l: { textCase?: string; allCaps?: boolean }): CaseMode {
  if (l.textCase === 'upper' || l.textCase === 'title' || l.textCase === 'sentence')
    return l.textCase;
  return l.allCaps ? 'upper' : 'none';
}

function columnAlign(zoneKey: string): AlignMode {
  return zoneKey.endsWith('Center') ? 'center' : zoneKey.endsWith('Right') ? 'right' : 'left';
}

/** The EFFECTIVE values of the selection, per control. */
export function readBar(forma: StandardConfig, keys: string[]): BarReading {
  const t = targetsOf(forma, keys);
  const geo = resolvePageGeometry(forma);
  const typoFont = geo.bodyFont;
  const typoSize = geo.bodyFontSizePt;

  const font: Array<string | undefined> = [];
  const size: number[] = [];
  const bold: boolean[] = [];
  const italic: boolean[] = [];
  const underline: boolean[] = [];
  const textCase: CaseMode[] = [];
  const color: string[] = [];
  const align: AlignMode[] = [];
  const before: number[] = [];
  const after: number[] = [];

  for (const i of t.levels) {
    const l = forma.levels[i];
    font.push(l.font || typoFont);
    size.push(l.fontSize || typoSize);
    bold.push(!!l.bold);
    italic.push(!!l.italic);
    underline.push(!!l.underline);
    textCase.push(caseOfLevel(l));
    color.push((l.fontColor || AUTO_COLOR).replace('#', '').toLowerCase());
    align.push(l.justify || 'left');
    before.push(l.spaceBefore || 0);
    after.push(l.spaceAfter || 0);
  }
  for (const tk of t.tokens) {
    const v = readZone(forma, tk.zoneKey)[tk.idx];
    const { mods } = parseTokenMods(v);
    const st = readTokenStyle(forma, tk.zoneKey, tk.idx);
    font.push(st?.font || typoFont);
    size.push(modSizePt(mods) ?? typoSize);
    bold.push(mods.has('bold'));
    italic.push(mods.has('italic'));
    underline.push(mods.has('underline'));
    textCase.push(
      mods.has('caps')
        ? 'upper'
        : mods.has('titlecase')
          ? 'title'
          : mods.has('sentencecase')
            ? 'sentence'
            : 'none'
    );
    const cm = [...mods].find((m) => m.startsWith('color:'));
    color.push(cm ? cm.slice(6).toLowerCase() : AUTO_COLOR);
    align.push(st?.align || columnAlign(tk.zoneKey));
    let sb = 0;
    let sa = 0;
    for (const m of mods) {
      if (m.startsWith('sb:')) sb = parseFloat(m.slice(3)) || 0;
      else if (m.startsWith('sa:')) sa = parseFloat(m.slice(3)) || 0;
    }
    before.push(sb);
    after.push(sa);
  }
  if (t.eos) {
    const es: TextStyle = forma.special?.eosStyle || {};
    font.push(es.font || typoFont);
    size.push(es.size || typoSize);
    bold.push(!!es.bold);
    italic.push(!!es.italic);
    underline.push(!!es.underline);
    textCase.push(es.textCase || 'none');
    color.push((es.color || AUTO_COLOR).replace('#', '').toLowerCase());
    align.push(es.align || 'center');
    before.push(readZoneSpace(forma, 'eos', 'before'));
    // The EOS line has no "after" gap of its own — it simply doesn't vote.
  }

  const count = t.levels.length + t.tokens.length + (t.eos ? 1 : 0);
  return {
    count,
    font: agg(font, sameCI),
    size: agg(size, same),
    bold: agg(bold, same),
    italic: agg(italic, same),
    underline: agg(underline, same),
    textCase: agg(textCase, same),
    color: agg(color, sameCI),
    align: agg(align, same),
    before: agg(before, same),
    after: agg(after, same),
    line: count ? { mixed: false, value: forma.docSettings?.lineSpacing || 1 } : agg([], same),
  };
}

/* ─── Writing ─── */

function rebuild(base: string, mods: Set<string>): string {
  if (mods.size === 0) return base;
  const modStr = [...mods].join(',');
  return base.endsWith('}') ? base.slice(0, -1) + '|' + modStr + '}' : base + '|' + modStr;
}

function editMods(v: string, fn: (mods: Set<string>) => void): string {
  if (!isTextToken(v)) return v;
  const { base, mods } = parseTokenMods(v);
  const next = new Set(mods);
  fn(next);
  return rebuild(base, next);
}

function dropPrefix(mods: Set<string>, prefix: string) {
  for (const m of [...mods]) if (m.startsWith(prefix)) mods.delete(m);
}

/** The in-string edit for one token value, or null when the property lives
 *  outside the string (font, align) or is not a token property (line). */
function tokenValueEdit(edit: BarEdit): ((v: string) => string) | null {
  switch (edit.prop) {
    case 'bold':
    case 'italic':
    case 'underline': {
      const on = edit.value;
      const name = edit.prop;
      return (v) =>
        editMods(v, (m) => {
          if (on) m.add(name);
          else m.delete(name);
        });
    }
    case 'size': {
      const pt = Math.min(SIZE_MAX, Math.max(SIZE_MIN, edit.value));
      return (v) =>
        editMods(v, (m) => {
          dropPrefix(m, 'size:');
          m.add('size:' + pt);
        });
    }
    case 'textCase': {
      const mode = edit.value;
      return (v) =>
        editMods(v, (m) => {
          m.delete('caps');
          m.delete('titlecase');
          m.delete('sentencecase');
          if (mode === 'upper') m.add('caps');
          else if (mode === 'title') m.add('titlecase');
          else if (mode === 'sentence') m.add('sentencecase');
        });
    }
    case 'color': {
      const hex = edit.value.replace('#', '').toLowerCase();
      return (v) =>
        editMods(v, (m) => {
          dropPrefix(m, 'color:');
          m.add('color:' + hex);
        });
    }
    case 'before':
    case 'after': {
      const pre = edit.prop === 'before' ? 'sb:' : 'sa:';
      const pt = Math.min(SPACE_MAX, Math.max(0, edit.value));
      return (v) =>
        editMods(v, (m) => {
          dropPrefix(m, pre);
          if (pt > 0) m.add(pre + pt);
        });
    }
    case 'reset':
      return (v) => editMods(v, (m) => dropPrefix(m, 'size:'));
    default:
      return null;
  }
}

function patchLevel(l: any, edit: BarEdit): any {
  switch (edit.prop) {
    case 'font':
      return { ...l, font: edit.value };
    case 'size':
      return { ...l, fontSize: Math.min(LEVEL_SIZE_MAX, Math.max(SIZE_MIN, edit.value)) };
    case 'bold':
    case 'italic':
    case 'underline':
      return { ...l, [edit.prop]: edit.value };
    case 'textCase':
      // D4: the 4-way case writes `textCase`, and the legacy `allCaps` flag
      // is kept in step for export compatibility.
      return {
        ...l,
        textCase: edit.value === 'none' ? undefined : edit.value,
        allCaps: edit.value === 'upper',
      };
    case 'color':
      return { ...l, fontColor: edit.value.replace('#', '').toLowerCase() };
    case 'align':
      return { ...l, justify: edit.value === 'left' ? undefined : edit.value };
    case 'before':
      return { ...l, spaceBefore: Math.min(SPACE_MAX, Math.max(0, edit.value)) };
    case 'after':
      return { ...l, spaceAfter: Math.min(SPACE_MAX, Math.max(0, edit.value)) };
    case 'reset': {
      // Same transform as the Levels panel's old switch: drop the keys
      // outright so nothing serializes as null.
      const next = { ...l };
      delete next.font;
      delete next.fontSize;
      return next;
    }
    default:
      return l;
  }
}

function patchEos(es: TextStyle, edit: BarEdit): TextStyle {
  const next: TextStyle = { ...es };
  const set = <K extends keyof TextStyle>(k: K, v: TextStyle[K] | undefined) => {
    if (v === undefined || v === false) delete next[k];
    else next[k] = v;
  };
  switch (edit.prop) {
    case 'font':
      set('font', edit.value);
      break;
    case 'size':
      set('size', Math.min(SIZE_MAX, Math.max(SIZE_MIN, edit.value)));
      break;
    case 'bold':
    case 'italic':
    case 'underline':
      set(edit.prop, edit.value);
      break;
    case 'textCase':
      set('textCase', edit.value === 'none' ? undefined : edit.value);
      break;
    case 'color':
      set('color', edit.value.replace('#', '').toLowerCase());
      break;
    case 'align':
      set('align', edit.value);
      break;
    case 'reset':
      delete next.font;
      delete next.size;
      break;
    default:
      break;
  }
  return next;
}

/** Apply ONE bar edit to every formattable thing in the selection. Pure;
 *  the caller hands the result to a single `edit()` so it is one undo step. */
export function applyBarEdit(forma: StandardConfig, keys: string[], edit: BarEdit): StandardConfig {
  if (edit.prop === 'line') {
    const v = Math.min(3, Math.max(1, edit.value || 1));
    return { ...forma, docSettings: { ...forma.docSettings, lineSpacing: v } };
  }
  const t = targetsOf(forma, keys);
  let next = forma;

  if (t.levels.length) {
    const levels = [...next.levels];
    for (const i of t.levels) levels[i] = patchLevel(levels[i], edit);
    next = { ...next, levels };
  }

  if (t.tokens.length) {
    const tokenKeys = t.tokens.map((x) => x.key);
    const fn = tokenValueEdit(edit);
    if (fn) next = zoneApplyToKeys(next, tokenKeys, fn);
    // font + align live beside the token, index-keyed (D3).
    if (edit.prop === 'font' || edit.prop === 'align' || edit.prop === 'reset') {
      for (const tk of t.tokens) {
        const cur: TextStyle = readTokenStyle(next, tk.zoneKey, tk.idx) || {};
        const upd: TextStyle = { ...cur };
        if (edit.prop === 'font') upd.font = edit.value;
        else if (edit.prop === 'align') upd.align = edit.value;
        else delete upd.font; // reset: Typography font again
        next = writeTokenStyle(next, tk.zoneKey, tk.idx, upd);
      }
    }
  }

  if (t.eos) {
    if (edit.prop === 'before') {
      const cur = readZoneSpace(next, 'eos', 'before');
      next = writeZoneSpace(next, 'eos', 'before', edit.value, eosDragMax(cur));
    } else if (edit.prop !== 'after') {
      const es = patchEos(next.special?.eosStyle || {}, edit);
      const special = { ...next.special };
      if (Object.keys(es).length) special.eosStyle = es;
      else delete special.eosStyle;
      next = { ...next, special };
    }
  }
  return next;
}
